<script setup lang="ts">
import { computed } from 'vue';
import type { MenuItem } from '@/services/orderService';

interface Props {
  actionType: string;
  formData: {
    product_condition: {
      menu_item_ids: number[];
      category_ids: number[];
      min_quantity?: number;
      max_quantity?: number;
    };
  };
  menuItems: MenuItem[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:formData': [value: any];
}>();

const formData = computed({
  get: () => props.formData,
  set: (value) => emit('update:formData', value)
});

const shouldShowProductCondition = computed(() => {
  return ['buy_x_get_y', 'fixed_price', 'free_item', 'buy_x_for_y_price', 'bundle_price'].includes(props.actionType);
});

const getQuantityPlaceholder = () => {
  switch (props.actionType) {
    case 'buy_x_get_y':
      return 'Ej: 2 (para 2x1)';
    case 'fixed_price':
      return 'Ej: 2 (cantidad de productos en el combo)';
    case 'free_item':
      return 'Ej: 1 (mínimo 1 producto para obtener el gratis)';
    case 'buy_x_for_y_price':
      return 'Ej: 2 (comprar 2 por precio especial)';
    case 'bundle_price':
      return 'Ej: 4 (mínimo 4 productos para el paquete)';
    default:
      return 'Ej: 1';
  }
};

const getQuantityDescription = () => {
  switch (props.actionType) {
    case 'buy_x_get_y':
      return 'Cantidad mínima de productos seleccionados que el cliente debe comprar';
    case 'fixed_price':
      return 'Cantidad de productos incluidos en el precio fijo';
    case 'free_item':
      return 'Cantidad mínima de productos requeridos para obtener el item gratis';
    case 'buy_x_for_y_price':
      return 'Cantidad de productos que se comprarán al precio especial';
    case 'bundle_price':
      return 'Cantidad mínima de productos para formar el paquete';
    default:
      return 'Cantidad mínima requerida';
  }
};
</script>

<template>
  <div class="border-t border-gray-200 dark:border-gray-700 pt-6">
    <h4 class="text-lg font-medium text-gray-900 dark:text-white mb-4">
      Condiciones de Productos
    </h4>
    
    <div
      v-if="shouldShowProductCondition"
      class="space-y-4"
    >
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Productos que aplican para la promoción
        </label>
        <div class="space-y-2">
          <div class="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2">
            <div class="grid grid-cols-3 gap-2">
              <div
                v-for="item in menuItems"
                :key="item.id"
                class="flex items-center"
              >
                <input
                  :checked="formData.product_condition.menu_item_ids.includes(item.id)"
                  type="checkbox"
                  class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mr-2"
                  @change="
                    ($event.target as HTMLInputElement).checked 
                      ? formData.product_condition.menu_item_ids.push(item.id)
                      : formData.product_condition.menu_item_ids = formData.product_condition.menu_item_ids.filter(id => id !== item.id)
                  "
                >
                <label class="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  {{ item.name }}
                </label>
              </div>
            </div>
          </div>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            Selecciona los productos que aplicarán para esta promoción
          </p>
        </div>
      </div>
      
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Cantidad mínima requerida
        </label>
        <input
          :value="formData.product_condition.min_quantity"
          type="number"
          min="1"
          :placeholder="getQuantityPlaceholder()"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          @input="formData.product_condition.min_quantity = Number(($event.target as HTMLInputElement).value)"
        >
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {{ getQuantityDescription() }}
        </p>
      </div>
      
      <!-- Max quantity solo para bundle_price -->
      <div v-if="actionType === 'bundle_price'">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Cantidad máxima de productos
        </label>
        <input
          :value="formData.product_condition.max_quantity"
          type="number"
          min="1"
          placeholder="Ej: 6 (máximo 6 productos en el paquete)"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          @input="formData.product_condition.max_quantity = Number(($event.target as HTMLInputElement).value)"
        >
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Cantidad máxima de productos permitidos en el paquete
        </p>
      </div>
    </div>
    
    <div
      v-else
      class="text-sm text-gray-500 dark:text-gray-400"
    >
      <p>Este tipo de promoción no requiere condiciones de productos específicas.</p>
    </div>
  </div>
</template>
